import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import NotFoundPage from './pages/NotFoundPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import EmailVerificationRibbon from './components/common/EmailVerificationRibbon';
import AuthDebug from './components/debug/AuthDebug';
import MainPage from './components/main-page/MainPage';
import { StoreApp } from './store';
import LoginPage from './components/authentication/login/LoginPage';
import RegisterPage from './components/authentication/register/RegisterPage';
import ForgotPasswordPage from './components/authentication/forgot-password/ForgotPasswordPage';
import EmailVerificationPage from './components/authentication/verify-email/EmailVerificationPage';
import VerifyEmailRouteHandler from './components/authentication/verify-email/VerifyEmailRouteHandler';
import ProfilePage from './components/authentication/profile/ProfilePage';
import SocietiesPage from './pages/SocietiesPage';
import SocietyDetailPage from './pages/SocietyDetailPage';
import BusinessesPage from './pages/BusinessesPage';
import BusinessDetailPage from './pages/BusinessDetailPage';
import BusinessCreatePage from './pages/BusinessCreatePage';
import BusinessDashboardPage from './pages/BusinessDashboardPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import AdsPage from './pages/AdsPage';
import AdDetailPage from './pages/AdDetailPage';
import PostDetailPage from './pages/PostDetailPage';
import PostsPage from './pages/PostsPage';
import PostCreatePage from './pages/PostCreatePage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import PropertyCreatePage from './pages/PropertyCreatePage';
import EventCreatePage from './pages/EventCreatePage';
import EventsListPage from './pages/EventsListPage';
import AdCreatePage from './pages/AdCreatePage';
import FreeItemCreatePage from './pages/FreeItemCreatePage';
import InstituteCreatePage from './pages/InstituteCreatePage';
import InstitutesPage from './pages/InstitutesPage';
import InstituteEditPage from './pages/InstituteEditPage';
import MosqueCreatePage from './pages/MosqueCreatePage';
import MosqueEditPage from './pages/MosqueEditPage';
import PlaceCreatePage from './pages/PlaceCreatePage';
// Note: Some page components don't exist yet, so we'll use existing ones or create placeholders
import InstituteDetailPage from './pages/InstituteDetailPage';
import MosqueDetailPage from './pages/MosqueDetailPage';
import MosquesPage from './pages/MosquesPage';
import ClubDetailPage from './pages/ClubDetailPage';
import IslamPage from './pages/IslamPage';
import IslamArticlesPage from './pages/islam/IslamArticlesPage';
import IslamArticleDetailPage from './pages/islam/IslamArticleDetailPage';
import IslamSectionPage from './pages/IslamSectionPage';
import ExplorePage from './pages/ExplorePage';
import CommunityPage from './pages/CommunityPage';
import TurfsPage from './pages/TurfsPage';
import ScrapperPage from './pages/ScrapperPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminSocietyCreatePage from './pages/AdminSocietyCreatePage';
import CreateAnnouncementPage from './pages/CreateAnnouncementPage';
import ManageAnnouncementsPage from './pages/ManageAnnouncementsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import MessagesPage from './pages/MessagesPage';
import ConversationPage from './pages/ConversationPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AboutContact from './pages/AboutContact';
import DeadlyCrossingHomePage from './pages/deadlyCrossing/DeadlyCrossingHomePage';
import DeadlyCrossingPrivacyPage from './pages/deadlyCrossing/DeadlyCrossingPrivacyPage';
import DeadlyCrossingSupportPage from './pages/deadlyCrossing/DeadlyCrossingSupportPage';
import PointsPage from './pages/points/PointsPage';
import PointCreatePage from './pages/points/PointCreatePage';
import PointDetailPage from './pages/points/PointDetailPage';
import PointEditPage from './pages/points/PointEditPage';
import VisaPage from './pages/VisaPage';
import ImmigrationNewsDetailPage from './pages/ImmigrationNewsDetailPage';
import VisaDetailsPage from './pages/VisaDetailsPage';
import PolicyDetailsPage from './pages/PolicyDetailsPage';
import ContactTravelPage from './pages/ContactTravelPage';
import ContactVisaPage from './pages/ContactVisaPage';
import HotelCreatePage from './pages/HotelCreatePage';
import HotelDetailPage from './pages/HotelDetailPage';
import Header from './components/main-page/Header';
import Footer from './components/main-page/Footer';
import BottomNav from './components/main-page/BottomNav';
import { navigateToHome } from './utils/homeNavigation';

/** Wraps auth pages (login, register, forgot-password) with Header and Footer and keeps URL as /login, /register, etc. */
function AuthShellLayout({ children }) {
  const navigate = useNavigate();
  const onNavigate = (page, data = {}) => {
    if (page === 'main') navigateToHome(navigate);
    else if (page === 'login') navigate('/login');
    else if (page === 'register') navigate('/register');
    else if (page === 'forgot-password') navigate('/forgot-password');
    else if (page === 'profile') navigate('/?auth=profile');
    else if (page === 'verify-email') navigate(`/verify-email${data.email ? `?email=${encodeURIComponent(data.email)}` : ''}`);
    else navigate('/');
  };
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Header onNavigate={onNavigate} />
      <main className="flex-grow pb-16 md:pb-0">{React.cloneElement(children, { onNavigate })}</main>
      <Footer onNavigate={onNavigate} />
      <BottomNav />
    </div>
  );
}

function AppContent() {
  const [currentPage, setCurrentPage] = useState('main');
  const [pageData, setPageData] = useState({});
  const { setPreviousPage, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle URL parameters for authentication; redirect ?auth=login etc. to /login so URL and nav show correctly
  useEffect(() => {
    if (isLoading) return;
    const urlParams = new URLSearchParams(location.search);
    const authParam = urlParams.get('auth');

    if (isAuthenticated && ['login', 'register', 'forgot-password'].includes(authParam)) {
      navigate('/', { replace: true });
      setCurrentPage('main');
      return;
    }
    if (!isAuthenticated && authParam === 'profile') {
      navigate('/login', { replace: true });
      return;
    }
    // Redirect ?auth=login|register|forgot-password to /login, /register, /forgot-password so URL and header/nav show
    if (location.pathname === '/' && authParam && !isAuthenticated) {
      if (authParam === 'login') {
        navigate('/login', { replace: true });
        return;
      }
      if (authParam === 'register') {
        navigate('/register', { replace: true });
        return;
      }
      if (authParam === 'forgot-password') {
        navigate('/forgot-password', { replace: true });
        return;
      }
    }
    if (location.pathname !== '/') return;
    if (!isAuthenticated) {
      setCurrentPage('main');
    } else {
      if (authParam === 'profile') setCurrentPage('profile');
      else setCurrentPage('main');
    }
  }, [location, isAuthenticated, isLoading, navigate]);

  const navigateTo = (page, data = {}) => {
    // Store the current page as previous page when navigating to auth pages
    if (['login', 'register', 'forgot-password'].includes(page)) {
      setPreviousPage(currentPage);
    }
    setCurrentPage(page);
    setPageData(data);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'login':
        return <LoginPage onNavigate={navigateTo} />;
      case 'register':
        return <RegisterPage onNavigate={navigateTo} />;
      case 'forgot-password':
        return <ForgotPasswordPage onNavigate={navigateTo} />;
      case 'verify-email':
        return <EmailVerificationPage onNavigate={navigateTo} email={pageData.email} />;
      case 'profile':
        return <ProfilePage onNavigate={navigateTo} />;
      case 'main':
      default:
        return <MainPage onNavigate={navigateTo} />;
    }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <EmailVerificationRibbon onNavigate={navigateTo} />
      <div className="flex-grow flex flex-col min-h-0">{renderPage()}</div>
      <BottomNav />
      {/* Debug component - only show in development */}
      {process.env.NODE_ENV === 'development' && <AuthDebug />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <Router>
            <Routes>
              {/* Original app routes */}
              <Route path="/" element={<AppContent />} />

              {/* Auth pages: URL stays /login, /register, etc. and Header/Footer are shown */}
              <Route path="/login" element={<AuthShellLayout><LoginPage /></AuthShellLayout>} />
              <Route path="/register" element={<AuthShellLayout><RegisterPage /></AuthShellLayout>} />
              <Route path="/signup" element={<Navigate to="/register" replace />} />
              <Route path="/forgot-password" element={<AuthShellLayout><ForgotPasswordPage /></AuthShellLayout>} />
              <Route path="/verify-email" element={<VerifyEmailRouteHandler />} />
              
              {/* Explore page (Discover hub) */}
              <Route path="/explore" element={<ExplorePage />} />

              {/* Community hub (Societies, Posts) */}
              <Route path="/community" element={<CommunityPage />} />

              {/* Societies page */}
              <Route path="/societies" element={<SocietiesPage />} />
              
              {/* Society detail page */}
              <Route path="/societies/:id" element={<SocietyDetailPage />} />
              
              {/* Society tab pages */}
              <Route path="/societies/:id/announcements" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/posts" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/businesses" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/events" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/property" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/ads" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/free-items" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/institutes" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/mosques" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/places" element={<SocietyDetailPage />} />
              <Route path="/societies/:id/reviews" element={<SocietyDetailPage />} />
              
              {/* Society-specific ads */}
              <Route path="/societies/:societySlug/ads" element={<AdsPage />} />
              <Route path="/societies/:societySlug/ads/create" element={<AdCreatePage />} />
              <Route path="/societies/:societySlug/ads/:adSlug/edit" element={<AdCreatePage />} />
              <Route path="/societies/:societySlug/ads/:adSlug" element={<AdDetailPage />} />
              
              {/* Society-specific events */}
              <Route path="/societies/:societySlug/events" element={<EventsPage />} />
              <Route path="/societies/:societySlug/events/create" element={<EventCreatePage />} />
              <Route path="/societies/:societySlug/events/:eventSlug/edit" element={<EventCreatePage />} />
              <Route path="/societies/:societySlug/events/:eventSlug" element={<EventDetailPage />} />
              
              {/* Society-specific businesses */}
              <Route path="/societies/:societySlug/businesses" element={<BusinessesPage />} />
              <Route path="/societies/:societySlug/businesses/create" element={<BusinessCreatePage />} />
              <Route path="/societies/:societySlug/businesses/:businessSlug" element={<BusinessDetailPage />} />
              
              {/* Society-specific posts */}
              <Route path="/societies/:societySlug/posts" element={<PostsPage />} />
              <Route path="/societies/:societySlug/posts/create" element={<PostCreatePage />} />
              <Route path="/societies/:societySlug/posts/:postSlug/edit" element={<PostCreatePage />} />
              <Route path="/societies/:societySlug/posts/:postSlug" element={<PostDetailPage />} />
              
              {/* Society-specific properties */}
              <Route path="/societies/:societySlug/properties" element={<PropertiesPage />} />
              <Route path="/societies/:societySlug/properties/create" element={<PropertyCreatePage />} />
              <Route path="/societies/:societySlug/properties/:propertySlug" element={<PropertyDetailPage />} />
              
              {/* Society-specific free items */}
              <Route path="/societies/:societySlug/free" element={<SocietyDetailPage />} />
              <Route path="/societies/:societySlug/free/create" element={<FreeItemCreatePage />} />
              
              {/* Society-specific institutes */}
              <Route path="/societies/:societySlug/institutes" element={<SocietyDetailPage />} />
              <Route path="/societies/:societySlug/institutes/create" element={<InstituteCreatePage />} />
              <Route path="/societies/:societySlug/institutes/:instituteSlug" element={<InstituteDetailPage />} />
              
              {/* Society-specific mosques */}
              <Route path="/societies/:societySlug/mosques" element={<SocietyDetailPage />} />
              <Route path="/societies/:societySlug/mosques/create" element={<MosqueCreatePage />} />
              <Route path="/societies/:societySlug/mosques/:mosqueSlug/edit" element={<MosqueEditPage />} />
              <Route path="/societies/:societySlug/mosques/:mosqueSlug" element={<MosqueDetailPage />} />
              
              {/* Society-specific places */}
              <Route path="/societies/:societySlug/places" element={<SocietyDetailPage />} />
              <Route path="/societies/:societySlug/places/create" element={<PlaceCreatePage />} />
              <Route path="/societies/:societySlug/places/:placeSlug" element={<SocietyDetailPage />} />
              
              {/* Businesses page */}
              <Route path="/businesses" element={<BusinessesPage />} />
              
              {/* Business detail page */}
              <Route path="/businesses/:businessSlug" element={<BusinessDetailPage />} />
              
              {/* Business create page */}
              <Route path="/businesses/create" element={<BusinessCreatePage />} />
              
              {/* Business edit page */}
              <Route path="/businesses/:businessSlug/edit" element={<BusinessCreatePage />} />

              {/* Owner dashboard (module id in the URL — linkable, back-button safe) */}
              <Route path="/businesses/:businessSlug/dashboard" element={<BusinessDashboardPage />} />
              <Route path="/businesses/:businessSlug/dashboard/:moduleId" element={<BusinessDashboardPage />} />
              
              {/* Events page */}
              <Route path="/events" element={<EventsPage />} />
              
              {/* Event listing pages (must be before :id catch-all) */}
              <Route path="/events/upcoming" element={<EventsListPage timeFrame="upcoming" />} />
              <Route path="/events/recent" element={<EventsListPage timeFrame="recent" />} />

              {/* Event create page */}
              <Route path="/events/create" element={<EventCreatePage />} />

              {/* Event edit page (before :id detail catch-all) */}
              <Route path="/events/:id/edit" element={<EventCreatePage />} />
              
              {/* Event detail page */}
              <Route path="/events/:id" element={<EventDetailPage />} />
              
              {/* Ads page */}
              <Route path="/ads" element={<AdsPage />} />

              {/* Points (formerly Travel) & Visa pages */}
              <Route path="/points" element={<PointsPage />} />
              <Route path="/points/contact" element={<ContactTravelPage />} />
              <Route path="/visa" element={<VisaPage />} />
              <Route path="/visa/contact" element={<ContactVisaPage />} />
              <Route path="/immigration-news/:id" element={<ImmigrationNewsDetailPage />} />
              <Route path="/visa-details/:slug" element={<VisaDetailsPage />} />
              <Route path="/policy-details/:slug" element={<PolicyDetailsPage />} />
              
              {/* Points pages */}
              <Route path="/points/create" element={<PointCreatePage />} />
              <Route path="/points/:pointSlug/edit" element={<PointEditPage />} />
              <Route path="/points/:pointSlug" element={<PointDetailPage />} />
              <Route path="/hotels/create" element={<HotelCreatePage />} />
              <Route path="/hotels/:hotelSlug" element={<HotelDetailPage />} />
              
              {/* Ad create page */}
              <Route path="/ads/create" element={<AdCreatePage />} />
              {/* Ad edit page (reuses create form) */}
              <Route path="/ads/:adSlug/edit" element={<AdCreatePage />} />

              {/* Ad detail page */}
              <Route path="/ads/:id" element={<AdDetailPage />} />
              <Route path="/ads/:adSlug" element={<AdDetailPage />} />
              
              {/* Posts page */}
              <Route path="/posts" element={<PostsPage />} />
              
              {/* Post create page */}
              <Route path="/posts/create" element={<PostCreatePage />} />
              
              {/* Post edit page */}
              <Route path="/posts/:postSlug/edit" element={<PostCreatePage />} />
              
              {/* Post detail page */}
              <Route path="/posts/:postSlug" element={<PostDetailPage />} />
              
              {/* Properties page */}
              <Route path="/properties" element={<PropertiesPage />} />
              
              {/* Property create page */}
              <Route path="/properties/create" element={<PropertyCreatePage />} />
              
              {/* Property detail page */}
              <Route path="/properties/:propertySlug" element={<PropertyDetailPage />} />
              
              <Route path="/institutes" element={<InstitutesPage />} />
              <Route path="/institutes/:id/edit" element={<InstituteEditPage />} />
              <Route path="/institutes/:id" element={<InstituteDetailPage />} />
              
              {/* Mosques directory (exact path before slug routes) */}
              <Route path="/mosques" element={<MosquesPage />} />

              <Route path="/mosques/:mosqueSlug/edit" element={<MosqueEditPage />} />
              <Route path="/mosques/:mosqueSlug" element={<MosqueDetailPage />} />
              
              {/* Club detail page */}
              <Route path="/clubs/:id" element={<ClubDetailPage />} />
              
              {/* Islam hub + subsection experiences */}
              <Route path="/islam" element={<IslamPage />} />
              <Route path="/islam/articles" element={<IslamArticlesPage />} />
              <Route path="/islam/articles/:slug" element={<IslamArticleDetailPage />} />
              <Route path="/islam/:sectionSlug" element={<IslamSectionPage />} />
              
              {/* Turfs page */}
              <Route path="/turfs" element={<TurfsPage />} />

              {/* Internal directory scraper (not in main nav) */}
              <Route path="/scrapper" element={<ScrapperPage />} />
              
              {/* Admin dashboard */}
              <Route path="/admin-portal" element={<AdminDashboard />} />
              <Route path="/admin-portal/societies/create" element={<AdminSocietyCreatePage />} />
              
              {/* Announcement Management */}
              <Route path="/create-announcement" element={<CreateAnnouncementPage />} />
              <Route path="/manage-announcements" element={<ManageAnnouncementsPage />} />
              <Route path="/edit-announcement/:id" element={<CreateAnnouncementPage />} />
              
              {/* Notification Preferences */}
              <Route path="/notification-preferences" element={<NotificationPreferencesPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/messages/:conversationId" element={<ConversationPage />} />

              {/* Legal and info pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/about" element={<Navigate to="/contact?tab=about" replace />} />
              <Route path="/contact" element={<AboutContact />} />

              {/* Deadly Crossing (Zok Labs) — App Store / Google Play legal URLs */}
              <Route path="/deadly-crossing" element={<DeadlyCrossingHomePage />} />
              <Route path="/deadly-crossing/privacy" element={<DeadlyCrossingPrivacyPage />} />
              <Route path="/deadly-crossing/support" element={<DeadlyCrossingSupportPage />} />

              {/* Store routes - all store pages under /store */}
              <Route path="/store/*" element={<StoreApp />} />

              {/* Catch-all 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Router>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
);
}

export default App;
